/**
 * AI Scanner Component
 * Использует камеру или галерею для сканирования визиток с AI парсингом
 */

import React, { useState } from "react";
import { Section, ToolbarButton } from "./UI";
import { useToast } from "./Toast";
import { apiClient } from "../services/api";

/**
 * Сжимает изображение до указанного размера
 */
const compressImage = (dataUrl, mimeType, maxSizeMB = 6, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    console.log('🔄 compressImage: Starting compression...');
    console.log('🔄 Target max size:', maxSizeMB, 'MB');
    console.log('🔄 Quality:', quality);
    
    const img = new Image();
    img.onload = () => {
      console.log('🔄 Image loaded, dimensions:', img.width, 'x', img.height);
      
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Уменьшаем разрешение если слишком большое
      const maxDimension = 1920; // макс. 1920px по любой стороне
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
        console.log('🔄 Resizing to:', width, 'x', height);
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Конвертируем в JPEG для лучшего сжатия
      const outputFormat = 'image/jpeg';
      let compressedDataUrl = canvas.toDataURL(outputFormat, quality);
      
      console.log('✅ Compression complete');
      console.log('📊 Output format:', outputFormat);
      console.log('📊 Output size:', (compressedDataUrl.length / (1024 * 1024)).toFixed(2), 'MB');
      
      // Если всё ещё слишком большой, снижаем качество
      const sizeMB = compressedDataUrl.length / (1024 * 1024);
      if (sizeMB > maxSizeMB && quality > 0.3) {
        console.log('⚠️ Still too large, reducing quality...');
        return resolve(compressImage(dataUrl, mimeType, maxSizeMB, quality - 0.1));
      }
      
      resolve(compressedDataUrl);
    };
    
    img.onerror = (err) => {
      console.error('❌ Image load error:', err);
      reject(err);
    };
    
    img.src = dataUrl;
  });
};

export function AiScanner({ setView, startAdd }) {
  const toast = useToast();
  const [processing, setProcessing] = useState(false);

  const handlePhotoSelect = async (preferCamera = false) => {
    console.log('📷 handlePhotoSelect called, preferCamera:', preferCamera);
    
    try {
      // Создаем input для выбора файла
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '*/*'; // Разрешаем ВСЕ форматы
      
      // Если нужна камера, добавляем атрибут capture
      if (preferCamera) {
        input.capture = 'environment'; // Задняя камера
      }
      
      console.log('📎 Input element created:', input.type, input.accept);

      input.onchange = async (e) => {
        console.log('📤 File input onChange triggered');
        console.log('📤 Event:', e);
        console.log('📤 Files:', e.target.files);
        
        const file = e.target.files?.[0];
        if (!file) {
          console.warn('⚠️ No file selected');
          return;
        }
        
        console.log('📁 File selected:', {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
        });
        
        // УБРАЛИ проверку MIME типа - разрешаем любые файлы
        console.log('✅ File type check DISABLED - accepting all formats');
        
        // Проверяем размер (максимум 30MB)
        const maxSize = 30 * 1024 * 1024; // 30MB - увеличенный лимит
        if (file.size > maxSize) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
          console.error('❌ File too large:', sizeMB, 'MB');
          toast.error(`⚠️ Файл слишком большой (${sizeMB}MB). Максимум 30MB`);
          return;
        }
        console.log('✅ File size check passed');

        try {
          setProcessing(true);
          console.log('🔄 Setting processing state to true');
          toast.success("🤖 AI анализирует визитку...");

          // Конвертируем в Base64
          console.log('🔄 Creating FileReader...');
          const reader = new FileReader();
          
          reader.onloadstart = () => {
            console.log('🔄 FileReader: load started');
          };
          
          reader.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              console.log(`🔄 FileReader progress: ${percentComplete.toFixed(1)}%`);
            }
          };
          
          reader.onload = async (event) => {
            console.log('✅ FileReader: load complete');
            const originalDataUrl = event.target?.result;
            const originalSize = originalDataUrl?.length || 0;
            const originalSizeMB = (originalSize / (1024 * 1024)).toFixed(2);
            
            console.log('📸 Original photo loaded');
            console.log('📊 Original size:', originalSize, 'bytes');
            console.log('📊 Original size:', originalSizeMB, 'MB');
            
            if (!originalDataUrl || typeof originalDataUrl !== 'string') {
              console.error('❌ Invalid photo data');
              toast.error("Ошибка обработки фото");
              setProcessing(false);
              return;
            }

            // СЖИМАЕМ ИЗОБРАЖЕНИЕ
            console.log('🔄 Starting image compression...');
            let photoDataUrl;
            try {
              photoDataUrl = await compressImage(originalDataUrl, file.type, 2, 0.7);
              const compressedSize = photoDataUrl.length;
              const compressedSizeMB = (compressedSize / (1024 * 1024)).toFixed(2);
              const compressionRatio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
              
              console.log('✅ Image compressed!');
              console.log('📊 Compressed size:', compressedSize, 'bytes');
              console.log('📊 Compressed size:', compressedSizeMB, 'MB');
              console.log('📊 Compression saved:', compressionRatio + '%');
            } catch (compressError) {
              console.error('❌ Compression failed, using original:', compressError);
              photoDataUrl = originalDataUrl;
            }

            try {
              const finalSize = photoDataUrl.length;
              const finalSizeMB = (finalSize / (1024 * 1024)).toFixed(2);
              
              console.log('🚀 Sending AI parse request...');
              console.log('🌐 API endpoint:', '/ai/parse-card');
              console.log('📦 Final payload size:', finalSize, 'bytes');
              console.log('📦 Final payload size:', finalSizeMB, 'MB');
              console.log('📦 Data preview:', photoDataUrl.substring(0, 100));
              console.log('📦 Full payload structure:', {
                image_base64_length: photoDataUrl.length,
                image_base64_preview: photoDataUrl.substring(0, 50),
                contains_data_prefix: photoDataUrl.includes('data:image'),
                contains_base64_marker: photoDataUrl.includes('base64,')
              });
              
              // Добавляем timeout для запроса (90 секунд)
              const controller = new AbortController();
              const timeoutId = setTimeout(() => {
                console.warn('⏰ Request timeout after 90s');
                controller.abort();
              }, 90000);
              
              console.log('📤 ABOUT TO CALL apiClient.post...');
              const requestStartTime = Date.now();
              
              try {
                // Отправляем на AI парсинг
                console.log('📤 Calling apiClient.post NOW...');
                const response = await apiClient.post('/ai/parse-card', {
                  image_base64: photoDataUrl
                });
                
                const requestDuration = Date.now() - requestStartTime;
                console.log(`✅ Request completed in ${requestDuration}ms`);
                
                clearTimeout(timeoutId);
                console.log('✅ AI parsing response received');
                console.log('📊 Response:', response);

                // response уже содержит { success, data, error }
                if (response.success) {
                  const parsedData = response.data;
                  
                  console.log('✅ Parsing successful!');
                  console.log('📋 Parsed data:', parsedData);
                  
                  // Проверяем, есть ли распарсенные данные
                  const hasData = Object.values(parsedData).some(val => val && val !== "");
                  
                  if (hasData) {
                    toast.success("✅ Данные успешно распарсены!");
                    // Открываем форму с предзаполненными данными и фото
                    startAdd([photoDataUrl], parsedData);
                  } else {
                    toast.error("⚠️ Не удалось распарсить данные");
                    // Открываем пустую форму с фото
                    startAdd([photoDataUrl]);
                  }
                } else {
                  const errorMsg = response.error || "Неизвестная ошибка";
                  console.error('❌ AI error:', errorMsg);
                  toast.error(`⚠️ AI ошибка: ${errorMsg}`);
                  // Открываем пустую форму с фото
                  startAdd([photoDataUrl]);
                }
              } catch (fetchError) {
                clearTimeout(timeoutId);
                
                console.error('💥 FETCH ERROR:', fetchError);
                console.error('💥 Error name:', fetchError.name);
                console.error('💥 Error message:', fetchError.message);
                console.error('💥 Error stack:', fetchError.stack);
                
                // Показываем alert для гарантированного отображения
                alert(`FETCH ERROR:\n${fetchError.name}\n${fetchError.message}`);
                
                if (fetchError.name === 'AbortError') {
                  console.error('⏰ Request timeout');
                  toast.error("⚠️ Запрос превысил время ожидания (90с)");
                } else {
                  throw fetchError;
                }
                // Открываем пустую форму с фото
                startAdd([photoDataUrl]);
              }
            } catch (error) {
              console.error('💥 AI parsing error:', error);
              console.error('💥 Error details:', {
                message: error.message,
                name: error.name,
                status: error.status,
                data: error.data,
                stack: error.stack
              });
              
              // Показываем alert для гарантированного отображения
              alert(`AI PARSING ERROR:\nName: ${error.name}\nMessage: ${error.message}\nStatus: ${error.status || 'N/A'}`);
              
              const errorMessage = error.data?.detail || error.message || "Неизвестная ошибка";
              toast.error(`⚠️ Ошибка AI: ${errorMessage}`);
              // Открываем пустую форму с фото
              startAdd([photoDataUrl]);
            } finally {
              setProcessing(false);
            }
          };

          reader.onerror = (error) => {
            console.error('❌ FileReader error:', error);
            console.error('❌ FileReader error details:', {
              error: reader.error,
              readyState: reader.readyState
            });
            toast.error("Ошибка чтения файла");
            setProcessing(false);
          };
          
          reader.onabort = () => {
            console.warn('⚠️ FileReader aborted');
            setProcessing(false);
          };

          console.log('🔄 Starting FileReader.readAsDataURL...');
          reader.readAsDataURL(file);
        } catch (error) {
          console.error("❌ Error processing photo:", error);
          console.error("❌ Error stack:", error.stack);
          toast.error("Ошибка обработки фото");
          setProcessing(false);
        }
      };

      // Программно кликаем на input
      console.log('📎 Triggering input.click()...');
      input.click();
      console.log('✅ input.click() triggered');

    } catch (error) {
      console.error("Error requesting photo:", error);
      toast.error("Ошибка запроса фото");
      setProcessing(false);
    }
  };

  return (
    <Section
      title="🤖 AI Сканер визиток"
      right={<ToolbarButton onClick={() => setView("home")} disabled={processing}>Назад</ToolbarButton>}
    >
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        <div className="text-6xl mb-4">📸</div>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-4">
          Выберите способ добавления визитки
        </p>
        
        {processing ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              AI анализирует визитку...
              <br />
              <span className="text-sm">Это может занять до 1 минуты</span>
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 w-full max-w-xs">
            {/* Кнопка Закрыть */}
            <button
              onClick={() => setView("home")}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-8 h-8 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="text-lg font-medium">Закрыть</span>
            </button>

            {/* Кнопка Галерея */}
            <button
              onClick={() => handlePhotoSelect(false)}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border-2 border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-lg font-medium text-blue-600 dark:text-blue-400">Из галереи</span>
            </button>

            {/* Кнопка Камера */}
            <button
              onClick={() => handlePhotoSelect(true)}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border-2 border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
            >
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-lg font-medium text-green-600 dark:text-green-400">Сфотографировать</span>
            </button>
          </div>
        )}
      </div>
    </Section>
  );
}
